import { ArrowRightLeft } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { PageTemplate, type Page, type PageStatus } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { MediaRichTextEditor } from '@/features/media';
import { slugify } from '@/features/products';
import {
  resolveSeoPreview,
  SeoOverrideFields,
  SeoPreviewCard,
  stripHtml,
  truncateWords,
  useSeoStoreContext,
} from '@/features/seo';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  pageFormSchema,
  type PageFormInput,
  type PageFormValues,
} from '../schemas/page-form-schema';
import { pagePath } from '../schemas/page-rules';

import { PageBlocksSection } from './page-blocks-section';

export const PAGE_FORM_ID = 'page-form';
const STATUS_ORDER: PageStatus[] = ['draft', 'published'];

export interface PageFormProps {
  storeId: string;
  /** The saved page; null while creating. */
  page: Page | null;
  defaultValues: PageFormInput;
  isSubmitting: boolean;
  onSubmit: (values: PageFormValues) => Promise<unknown>;
  dangerZone?: ReactNode;
}

function LivePreview({ storeId, page }: { storeId: string; page: Page | null }) {
  const context = useSeoStoreContext(storeId);
  const [title, handle, bodyHtml, status, faq, seo] = useWatch<
    PageFormInput,
    ['title', 'handle', 'bodyHtml', 'status', 'blocks.faq', 'seo']
  >({ name: ['title', 'handle', 'bodyHtml', 'status', 'blocks.faq', 'seo'] });
  const path = pagePath(handle.trim() ? handle : (page?.handle ?? ''));
  const description = bodyHtml.trim()
    ? truncateWords(stripHtml(bodyHtml), 300)
    : (faq[0]?.answer ?? null);
  const live = context
    ? resolveSeoPreview(
        {
          title,
          description,
          path,
          published: status === 'published',
          override: seo,
          image: null,
        },
        context,
      )
    : null;
  return <SeoPreviewCard live={live} saved={page?.resolvedSeo ?? null} path={path} />;
}

/**
 * Details, body, template blocks and the SEO override in the main column; status, save and the
 * live search preview in the side rail, bound to the same form.
 */
export function PageForm({
  storeId,
  page,
  defaultValues,
  isSubmitting,
  onSubmit,
  dangerZone,
}: PageFormProps) {
  const { t } = useTranslation(['pages', 'common']);
  const message = useErrorMessage();
  const form = useForm<PageFormInput, unknown, PageFormValues>({
    resolver: useI18nResolver(pageFormSchema),
    defaultValues,
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const [handleTouched, setHandleTouched] = useState(page !== null);
  const handle = form.watch('handle');
  const renamed = page !== null && handle.trim() !== '' && handle.trim() !== page.handle;
  const isDirty = form.formState.isDirty;
  const rootError = form.formState.errors.root?.server?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values);
      if (page) form.reset(values);
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  useSaveBar({ form, onSave: submit, saving: isSubmitting });

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <FormSection title={t('pages:sections.status')} tour="content.page-editor.save">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-2.5"
                        aria-label={t('pages:sections.status')}
                      >
                        {STATUS_ORDER.map((status) => (
                          <div key={status} className="flex items-start gap-2.5">
                            <RadioGroupItem
                              value={status}
                              id={`page-status-${status}`}
                              className="mt-0.5"
                            />
                            <div className="grid gap-0.5">
                              <Label htmlFor={`page-status-${status}`}>
                                {t(`pages:status.${status}`)}
                              </Label>
                              <span className="text-muted-foreground text-xs">
                                {t(`pages:statusHint.${status}`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Can permission="content.write">
                <div className="flex items-center gap-2 border-t pt-4">
                  <Button
                    type="submit"
                    form={PAGE_FORM_ID}
                    disabled={isSubmitting || (page !== null && !isDirty)}
                    className="flex-1"
                  >
                    {isSubmitting
                      ? t('common:actions.saving')
                      : page
                        ? t('common:actions.save')
                        : t('pages:create.submit')}
                  </Button>
                  {page ? (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={isSubmitting || !isDirty}
                      onClick={() => form.reset()}
                    >
                      {t('pages:actions.discard')}
                    </Button>
                  ) : null}
                </div>
              </Can>
            </FormSection>
            <div className="grid grid-cols-1" data-tour="content.page-editor.seo-preview">
              <LivePreview storeId={storeId} page={page} />
            </div>
            {dangerZone}
          </>
        }
      >
        <form id={PAGE_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection
            tour="content.page-editor.details"
            title={t('pages:sections.details')}
            description={t('pages:sections.detailsDescription')}
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('pages:fields.title')}</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus={page === null}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        if (!handleTouched) {
                          form.setValue('handle', slugify(e.target.value), { shouldDirty: true });
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="handle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('pages:fields.handle')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="font-mono text-[13px]"
                      onChange={(e) => {
                        setHandleTouched(true);
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('pages:fields.handleHint', { path: pagePath(field.value || 'handle') })}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {renamed ? (
              <Alert role="status" data-testid="handle-rename-hint">
                <ArrowRightLeft aria-hidden />
                <AlertDescription>
                  {t('pages:fields.renameHint', {
                    from: pagePath(page.handle),
                    to: pagePath(handle.trim()),
                  })}
                </AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="template"
              render={({ field }) => (
                <FormItem data-tour="content.page-editor.template">
                  <FormLabel>{t('pages:fields.template')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full sm:w-72">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(PageTemplate).map((template) => (
                        <SelectItem key={template} value={template}>
                          {t(`pages:template.${template}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>{t(`pages:templateHint.${field.value}`)}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <FormSection
            tour="content.page-editor.body"
            title={t('pages:sections.body')}
            description={t('pages:sections.bodyDescription')}
          >
            <FormField
              control={form.control}
              name="bodyHtml"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('pages:fields.bodyHtml')}</FormLabel>
                  <FormControl>
                    <MediaRichTextEditor
                      value={field.value}
                      onChange={(html) => field.onChange(html ?? '')}
                      onBlur={field.onBlur}
                      aria-label={t('pages:fields.bodyHtml')}
                    />
                  </FormControl>
                  <FormDescription>{t('pages:fields.bodyHtmlHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <div className="flex flex-col gap-6" data-tour="content.page-editor.blocks">
            <PageBlocksSection />
          </div>
          <SeoOverrideFields />
        </form>
      </FormLayout>
    </Form>
  );
}
